import React, { useState, useEffect, useContext } from 'react';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isString from 'lodash/isString';
import isObject from 'lodash/isObject';

import AddNewCarrier from './AddNewCarrier.component';
import { WrappedToolbar } from '../ui/toolbar/WrappedToolbar.component';
import { Section, SectionBody } from '../ui/layout/Section.component';
import Data from '../data';
import Grid from '../grid';
import Modal from '../modal';
import Session from '../utils/Session';
import useConstructor from '../utils/customHooks';
import Utils from '../utils';
import Util from '../utils/Util';
import Analytics from '../utils/Analytics';
import { SegmentEventTypes } from '../data/tracking';
import AddUpsCarrier from './UPS/AddUpsCarrier.component';
import { MessageModal } from '../modal/MessageModal.component';

const { ModalContext } = Modal;
const { GridPanel } = Grid;
const { AllCarriers, Carrier, Capability } = Data;
const { Communication, ErrorCodes } = Utils;

const allCarriers = AllCarriers.reduce(
	(_result, carrier) => ({
		..._result,
		[carrier.value]: carrier,
	}),
	{}
);

export const ManageCarrier = ({
	addCarrierCallback = () => {},
	noEligibleCarriersCallback = () => {},
	enterpriseLocation = {},
	enterpriseLocations = null,
}) => {
	const [action, setAction] = useState(null);
	// eslint-disable-next-line no-unused-vars
	const [selected, setSelected] = useState(null);
	const [capable, setCapable] = useState([]);
	const [configured, setConfigured] = useState([Carrier.USPS]);
	const [subscriberId, setSubscriberId] = useState(null);
	const [entLocation, setEntLocation] = useState(enterpriseLocation);
	const [carrierDetails, setCarrierDetails] = useState(null);

	const { showModal, showError } = useContext(ModalContext);

	const carrierType = Util.getQueryParamByName('carrier');
	const code = Util.getQueryParamByName('code');
	const authState = Util.getQueryParamByName('auth_state');

	const carrierRowData = carrierArray =>
		carrierArray.map(carrier => ({ carrier: allCarriers[carrier] }));

	const setCapableAndConfiguredCarriers = location => {
		const isEnterpriseLocation = !isEmpty(location);
		const accountInfo = isEnterpriseLocation ? location : Session.getAccountInfo();
		const configuredCarriers = isArray(accountInfo.configuredCarriers)
			? accountInfo.configuredCarriers
			: [];

		const combindedCapabilities = isEnterpriseLocation
			? [...location.Capabilities, ...location.InternalCapabilities]
			: [
					...accountInfo.capabilities,
					...accountInfo.internalAccountDetails.internalCapabilities,
			  ];
		const hasAccessToGlobalPost =
			combindedCapabilities.includes(Capability.Globalpost) ||
			combindedCapabilities.includes(Capability.GpSmartSaver);
		const capableCarriersArray = [];
		const configuredCarriersArray = [
			Carrier.USPS,
			...configuredCarriers.map(({ carrier }) => carrier),
		];

		const configuredCarriersMap = configuredCarriers.reduce((_configuredCarriers, carrier) => {
			_configuredCarriers[carrier.carrier] = carrier;
			return _configuredCarriers;
		}, {});

		setCarrierDetails(configuredCarriersMap);

		if (hasAccessToGlobalPost) {
			configuredCarriersArray.push(Carrier.GlobalPost);
		}

		if (
			combindedCapabilities.includes(Capability.Ups) &&
			!configuredCarriersArray.includes(Carrier.UPS)
		) {
			capableCarriersArray.push(Carrier.UPS);
		}

		if (
			combindedCapabilities.includes(Capability.DhlExpress) &&
			!configuredCarriersArray.includes(Carrier.DHLExpress)
		) {
			capableCarriersArray.push(Carrier.DHLExpress);
		}

		if (
			combindedCapabilities.includes(Capability.Fedex) &&
			!configuredCarriersArray.includes(Carrier.FedEx)
		) {
			capableCarriersArray.push(Carrier.FedEx);
		}

		// Bulk update but no Carriers are able to be added
		if (!isEmpty(enterpriseLocations) && capableCarriersArray.length === 0) {
			noEligibleCarriersCallback(false);
		}

		setCapable(capableCarriersArray);
		setConfigured(configuredCarriersArray);
	};

	const getSharedCapabilitiesAndCarriersForBulkUpdate = () => {
		const location = {
			capabilities: [],
			configuredCarriers: [],
		};

		location.Capabilities = Util.getSharedItemsFromArray(
			enterpriseLocations.map(({ Capabilities }) => Capabilities)
		);
		location.InternalCapabilities = Util.getSharedItemsFromArray(
			enterpriseLocations.map(({ InternalCapabilities }) => InternalCapabilities)
		);
		location.configuredCarriers = Util.getSharedObjectsFromArray(
			enterpriseLocations.map(({ ConfiguredCarriers = [] }) => ConfiguredCarriers),
			'carrier'
		);

		return location;
	};

	useConstructor(() => {
		// Load the selected location from localstorage since we can't use Session
		const storedLocation = JSON.parse(localStorage.getItem('selectedEnterpriseLocation'));
		if (storedLocation) {
			setEntLocation(storedLocation);
		}

		// Bulk Enterprise Location Update
		if (!isEmpty(enterpriseLocations)) {
			setCapableAndConfiguredCarriers(getSharedCapabilitiesAndCarriersForBulkUpdate());
		}
		// Single Enterprise Location Update
		else if (!isEmpty(entLocation)) {
			setCapableAndConfiguredCarriers(entLocation);

			// Save the selected location in localstorage since we can't use Session
			localStorage.setItem('selectedEnterpriseLocation', JSON.stringify(entLocation));
		}
		// default
		else {
			setCapableAndConfiguredCarriers();

			const id = Session.subscribe(() => {
				setCapableAndConfiguredCarriers();
			});

			setSubscriberId(id);
		}
	});

	useEffect(() => () => Session.unsubscribe(subscriberId), []);

	const completionCallback = (success, carrier, locationsLength) => {
		if (success) {
			Session.loadAccountInfo();

			if (enterpriseLocation) {
				setConfigured([...configured, carrier]);
			}
		}
		addCarrierCallback(success, carrier, locationsLength);
		setAction(null);
	};

	const addCarrierOnClick = () => {
		setAction('add');
	};

	const editCarrierOnClick = () => {
		setAction('edit');
	};

	const monoauthAddCarrier = async () => {
		const response = await Communication.monoauthAddCarrier({
			Code: code,
			AuthState: authState,
		});

		const brand = Session.getBrand().brandName;

		const isReauthFlow = !isEmpty(carrierDetails);

		if (Communication.isSuccessful(response)) {
			Session.loadAccountInfo();
			setAction(null);
			const title = `${carrierType.toUpperCase()} Account Authenticated!`;
			if (isReauthFlow) {
				showModal({
					component: MessageModal,
					data: {
						title,
						description: `You can resume use of your ${carrierType.toUpperCase()} account.`,
					},
				});
			} else {
				setConfigured([...configured, carrierType]);

				showModal({
					component: MessageModal,
					data: {
						title,
						description: `Your UPS account and rates are available in ${brand}.`,
					},
				});

				addCarrierCallback(true, carrierType, null);
			}
		} else if (response.ErrorCode.toString() === ErrorCodes.swsim.UPS_ADD_CARRIER_ERROR) {
			showError({
				component: MessageModal,
				data: {
					title: 'Unable to add UPS account',
					description: 'Please review the provided information and try again.',
				},
			});
		} else {
			showError({ response });
		}
	};

	const isCarrierEditEligible = selectedCarrier => {
		if (selectedCarrier?.value === Carrier.UPS) {
			const carrier = carrierDetails?.[selectedCarrier.value];

			return carrier?.userOwnedAccount === true;
		}
		return false;
	};

	useEffect(() => {
		if (isString(code) && isString(authState)) {
			monoauthAddCarrier();
		}
	}, []);

	const checkUps = !capable.includes(Carrier.UPS) || configured.includes(Carrier.UPS);
	const checkDhlx =
		!capable.includes(Carrier.DHLExpress) || configured.includes(Carrier.DHLExpress);
	const checkFedEx = !capable.includes(Carrier.FedEx) || configured.includes(Carrier.FedEx);
	const isAddDisabled = checkUps && checkDhlx && checkFedEx;
	const isEditEligible = isCarrierEditEligible(selected);
	const isSingleLocationSelected =
		isObject(enterpriseLocation) && Object.keys(enterpriseLocation).length > 0;

	// array of objects with button configurations
	const buttons = [
		{
			label: 'Add',
			icon: 'add-icon',
			onClick: addCarrierOnClick,
			disabled: isAddDisabled,
		},
		{
			label: 'Edit',
			onClick: editCarrierOnClick,
			disabled: !isEditEligible,
		},
		{
			label: 'Disconnect',
			disabled: true,
		},
	];
	return (
		<>
			{/* default view displaying the toolbar + grid */}
			{action === null && (
				<div className="common-carrier-manage-carrier">
					<Section>
						<WrappedToolbar buttons={buttons} dataqa="managecarrier" />
						<p className="header">Manage your linked Carrier accounts.</p>
						<SectionBody>
							<GridPanel
								rowData={carrierRowData(configured)}
								columnDefs={[
									{
										headerName: 'Carrier',
										valueGetter: 'data.carrier.displayName',
										sortable: false,
										resizable: false,
										flex: 1,
										suppressSizeToFit: false, // to fill the entire available space
										menuTabs: [], // to hide the column menu
										cellClass: ({ data: { carrier } }) => {
											const { value } = carrier || '';
											const carrierLogoCls = value.replace(/_/g, '-');

											return `${carrierLogoCls}-logo manage-carrier-row carrier-logo`;
										},
									},
								]}
								onSelectionChanged={selection => {
									// Add Segment tracking for when use selects a carrier
									Analytics.trackSegmentEvent({
										trackingData: {
											field_type: 'selection',
											field_name_dataqa: `manage-carriers-carrier-selection-${selection[0].data.carrier}`,
										},
										eventType: SegmentEventTypes.UserInteraction,
									});

									setSelected(selection[0].data.carrier);
								}}
								showPagination={false}
								dataqa="managecarrier"
							/>
						</SectionBody>
					</Section>
				</div>
			)}
			{/* clicking the Add button displays the carrier selection grid */}

			{action === 'add' && (
				<AddNewCarrier
					capable={capable}
					configured={configured}
					custId={entLocation ? entLocation.CustId : null}
					enterpriseLocations={enterpriseLocations || null}
					isSingleLocationSelected={isSingleLocationSelected}
					completionCallback={completionCallback}
				/>
			)}

			{action === 'edit' && selected.value === Carrier.UPS && (
				<AddUpsCarrier
					editCarrierDetails={carrierDetails[selected.value]}
					completionCallback={completionCallback}
					action={action}
				/>
			)}
		</>
	);
};
